/**
 * Turn stored accounts into the mailbox .txt Add Accounts reads back.
 * The live password, phone, and proxy replace copies inside the signup string.
 */
import { isValidPhone } from "./account-fields.js";

export interface ExportableAccount {
  username: string;
  password?: string | null;
  proxy?: string | null;
  signup_email_string?: string | null;
  signup_phone_string?: string | null;
  session_status?: string | null;
}

/**
 * Accounts to write. When Hide suspended accounts is on, banned rows are
 * left out. An explicit off keeps them.
 */
export function accountsForExport<T extends ExportableAccount>(
  accounts: T[],
  hideSuspended: boolean,
): T[] {
  if (!hideSuspended) return accounts;
  return accounts.filter(
    (account) =>
      String(account.session_status || "").toLowerCase() !== "banned",
  );
}

function exportPhone(raw: string | null | undefined): string | null {
  const value = String(raw || "").trim();
  if (!value) return null;
  const head = value.split(":")[0]?.trim() || "";
  const candidate = isValidPhone(value) ? value : isValidPhone(head) ? head : "";
  if (!candidate) return null;
  const digits = candidate.replace(/\D/g, "");
  return digits || null;
}

function signupExtras(
  signup: string | null | undefined,
  livePhone: string | null,
  liveProxy: string,
): string[] {
  const raw = String(signup || "").trim();
  if (!raw.includes("|")) return [];
  const extras: string[] = [];
  for (const part of raw.split("|").slice(1)) {
    const piece = part.trim();
    const eq = piece.indexOf("=");
    if (eq <= 0) continue;
    const key = piece.slice(0, eq).trim().toLowerCase();
    const value = piece.slice(eq + 1).trim();
    if (!value) continue;
    if ((key === "phone" || key === "mobile") && livePhone) continue;
    if (key === "proxy" && liveProxy) continue;
    extras.push(`${piece.slice(0, eq).trim()}=${value}`);
  }
  return extras;
}

/** One account, in the mailbox line shape. Blank usernames are skipped. */
export function formatAccountLine(account: ExportableAccount): string {
  const username = String(account.username || "").trim();
  if (!username) return "";
  const password = String(account.password ?? "");
  const phone = exportPhone(account.signup_phone_string);
  const proxy = String(account.proxy || "").trim();
  const parts = [
    `${username}:${password}`,
    ...signupExtras(account.signup_email_string, phone, proxy),
  ];
  if (phone) parts.push(`phone=${phone}`);
  if (proxy) parts.push(`proxy=${proxy}`);
  return parts.join("|");
}

/** Mailbox lines plus a trailing newline, or "" when there is nothing to write. */
export function formatAccountsTxt(accounts: ExportableAccount[]): string {
  const lines = accounts
    .map((account) => formatAccountLine(account))
    .filter(Boolean);
  if (!lines.length) return "";
  return `${lines.join("\n")}\n`;
}
